import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import axios from 'axios';
import { useAuth } from './auth/AuthContext';
import { SERVER_NAME } from './config';
import { getE3DNFTManagerContract } from './web3/contractInteractions';

const useStyles = makeStyles((theme) => ({
  dialog: {
    backgroundColor: '#1a1a2e',
    color: 'white',
    borderRadius: '8px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
    '& .MuiDialogContentText-root': {
      color: '#ffffff',
      fontSize: '1rem',
    },
  },
  title: {
    color: '#ff5252',
    fontWeight: 700,
    borderBottom: '1px solid #ff5252',
    padding: '10px 0',
    margin: '0 0 15px 0',
  },
  content: {
    padding: '10px 20px 20px 20px',
  },
  confirmationBox: {
    margin: '20px 0',
    padding: '18px',
    backgroundColor: 'rgba(255,82,82,0.15)',
    border: '1px solid rgba(255,82,82,0.4)',
    borderRadius: '6px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.2)',
  },
  warning: {
    color: '#ff6b6b',
    fontWeight: 600,
    fontSize: '1.05rem',
    marginBottom: '15px',
    textShadow: '0px 0px 1px rgba(255, 107, 107, 0.4)',
  },
  actionButton: {
    margin: theme.spacing(1),
  },
  cancelButton: {
    color: '#ffffff',
    borderColor: '#ffffff',
  },
  confirmButton: {
    backgroundColor: '#ff5252',
    color: '#ffffff',
    fontWeight: 'bold',
    boxShadow: '0 2px 8px rgba(255, 82, 82, 0.5)',
    '&:hover': {
      backgroundColor: '#ff7070',
    },
    '&:disabled': {
      backgroundColor: '#7a3838',
      color: '#d5d5d5',
    },
  },
  checkboxLabel: {
    color: '#ffffff',
    fontSize: '0.95em',
    fontWeight: 500,
  },
  checkbox: {
    color: '#ffffff',
    '&.Mui-checked': {
      color: '#ff6b6b',
    },
  },
  infoSection: {
    marginBottom: '15px',
  },
  processingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 0',
  },
  statusMessage: {
    marginTop: '15px',
    textAlign: 'center',
  },
  successMessage: {
    color: '#4caf50',
    fontWeight: 500,
    marginTop: '10px',
  },
  errorMessage: {
    color: '#ff5252',
    fontWeight: 500,
    marginTop: '10px',
  },
}));

export const UnsubscribeDialog = ({ open, onClose, userID: propUserID, username: propUsername }) => {
  const classes = useStyles();
  const { userID: contextUserID, userAddress, username: contextUsername, logout } = useAuth();
  
  // Get userID and username from all possible sources
  const localStorageUserID = localStorage.getItem('userID');
  const localStorageUsername = localStorage.getItem('username');
  
  // Use props if provided, otherwise fall back to context, and finally localStorage
  const userID = propUserID || contextUserID || localStorageUserID;
  const username = propUsername || contextUsername || localStorageUsername;
  
  console.log('UnsubscribeDialog sources:', { 
    propUserID, 
    contextUserID, 
    localStorageUserID,
    userID, // effective value
    propUsername,
    contextUsername,
    localStorageUsername,
    username // effective value
  });
  
  // Verify userID is valid on component mount
  useEffect(() => {
    if (open && (!userID || userID === 'undefined')) {
      console.error('Invalid or missing userID on UnsubscribeDialog mount.', { 
        propUserID, 
        contextUserID, 
        effectiveUserID: userID 
      });
    } else if (open) {
      console.log('UnsubscribeDialog opened with valid userID:', userID);
    }
  }, [open, userID, propUserID, contextUserID]);
  
  // State for the unsubscribe process
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState((!userID || userID === 'undefined') ? 'User ID is missing. Please log in again and retry.' : '');
  const [success, setSuccess] = useState(false);
  
  // Checkboxes for confirmation
  const [confirmUnderstand, setConfirmUnderstand] = useState(false);
  const [confirmNFT, setConfirmNFT] = useState(false);
  const [confirmFinal, setConfirmFinal] = useState(false);
  
  // Function to handle proper logout and UI reset
  const handleLogout = () => {
    console.log('Performing complete logout after account deletion');
    
    // Clear all authentication data from localStorage
    localStorage.removeItem('userID');
    localStorage.removeItem('username');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userAddress');
    
    // Call the logout function from context
    if (typeof logout === 'function') {
      logout();
    }
    
    // Force a page reload to ensure all components update their state
    setTimeout(() => {
      window.location.href = '/';
    }, 500);
  };
  
  const handleClose = () => {
    // If successful unsubscribe, perform complete logout
    if (success) {
      handleLogout();
    } else {
      onClose();
    }
  };
  
  const handleUnsubscribe = async () => {
    if (!confirmUnderstand || !confirmNFT || !confirmFinal) {
      setError('Please confirm all checkboxes to proceed with account deletion');
      return;
    }
    
    // Make one final attempt to get userID from all possible sources
    let effectiveUserID = userID;
    let effectiveUsername = username;
    
    if (!effectiveUserID) {
      // Try localStorage again
      const storedID = localStorage.getItem('userID');
      if (storedID) {
        console.log('Last chance: Found userID in localStorage:', storedID);
        effectiveUserID = storedID;
      }
    }
    
    if (!effectiveUsername) {
      // Try localStorage for username
      const storedUsername = localStorage.getItem('username');
      if (storedUsername) {
        console.log('Last chance: Found username in localStorage:', storedUsername);
        effectiveUsername = storedUsername;
      }
    }
    
    // Check if we have enough information to proceed
    const canProceed = effectiveUserID || effectiveUsername;
    
    if (!canProceed) {
      console.error('No identification method available. Cannot proceed with account deletion.');
      setError('Cannot identify your account. Please log in again and retry.');
      setIsProcessing(false);
      return;
    }

    console.log('Starting account deletion process for user:', { userID, username });
    setIsProcessing(true);
    setError('');
    setStatus('Starting unsubscribe process...');
    
    // Extract the base URL without any path segments for API endpoints
    console.log('Server URL from config:', SERVER_NAME);
    
    // Properly construct the endpoint URL to preserve the /apitest path
    // This ensures we're using the complete SERVER_NAME path
    const deleteUserEndpoint = SERVER_NAME.endsWith('/') 
      ? `${SERVER_NAME}deleteUser`  // If SERVER_NAME ends with '/', don't add another slash
      : `${SERVER_NAME}/deleteUser`; // Otherwise add the slash before 'deleteUser'
    
    console.log('Server URL from config:', SERVER_NAME);
    console.log('Correct API endpoint URL:', deleteUserEndpoint);
    
    try {
      // Step 1: Remove from NFT subscription list via smart contract (if connected to wallet)
      if (userAddress) {
        setStatus('Cancelling blockchain subscription...');
        try {
          console.log('Attempting to cancel blockchain subscription for wallet:', userAddress);
          const nftManagerContract = await getE3DNFTManagerContract();
          if (nftManagerContract) {
            const tx = await nftManagerContract.methods.cancelSubscription().send({ from: userAddress });
            console.log('Blockchain subscription cancelled successfully:', tx);
            setStatus('Blockchain subscription cancelled successfully');
          } else {
            console.warn('NFT Manager contract not available');
            setStatus('Warning: NFT Manager contract not available. Continuing with account deletion...');
          }
        } catch (error) {
          console.error('Error cancelling blockchain subscription:', error);
          setStatus('Warning: Blockchain subscription cancellation failed. Continuing with account deletion...');
        }
      } else {
        console.log('No wallet address connected. Skipping blockchain subscription cancellation.');
      }
      
      // Step 2: Remove user from database
      setStatus('Removing account from database...');
      console.log('Sending deleteUser request to server with:', { effectiveUserID, effectiveUsername });
      
      
      // Log complete request details for debugging
      console.log('Complete deletion request details:', {
        endpoint: deleteUserEndpoint,
        method: 'POST',
        data: {
          username: effectiveUsername || username,
          userID: effectiveUserID,
          _method: 'DELETE'
        },
        withCredentials: true
      });

      // Use POST method instead of DELETE to avoid CORS issues with preflight requests
      // Many servers have CORS configured for GET/POST but not properly for DELETE
      const response = await axios.post(deleteUserEndpoint, {
        username: effectiveUsername || username,  // The server expects 'username', not 'userID'
        userID: effectiveUserID,
        _method: 'DELETE',  // This signals to the server this is actually a DELETE request
        debugMode: true     // Add debug flag to see more details
      }, {
        withCredentials: true
      });
      
      console.log('Server response:', response);
      
      if (response.status === 200) {
        console.log('Account deletion successful!');
        setStatus('Account successfully deleted');
        setSuccess(true);
        
        // Show a brief success message before logging out
        alert('Your account has been successfully deleted. You will now be logged out.');
        
        // Log out immediately
        handleLogout();
        
        // Close the dialog
        onClose();
      } else {
        console.error('Unexpected server response status:', response.status);
        throw new Error('Server returned status: ' + response.status);
      }
    } catch (error) {
      console.error('Error during unsubscribe process:', error);
      let errorMessage = 'Failed to complete the unsubscribe process. Please try again or contact support.';
      let detailedError = '';
      
      // Log detailed error information
      if (error.response) {
        console.error('Response error data:', error.response.data);
        console.error('Response error status:', error.response.status);
        console.error('Response error headers:', error.response.headers);
        
        errorMessage = error.response.data?.message || errorMessage;
        
        // Check for specific error cases
        if (error.response.status === 404) {
          errorMessage = 'API endpoint not found. The server might be misconfigured.';
          console.error('404 Error Details: The server cannot find the requested resource', {
            endpoint: deleteUserEndpoint,
            serverName: SERVER_NAME
          });
        }
        
        // Get detailed error information
        if (error.response.data?.error) {
          detailedError = `Error details: ${error.response.data.error}`;
        }
        if (error.response.data?.errorType) {
          detailedError += ` (${error.response.data.errorType})`;
        }
      } else if (error.request) {
        console.error('Request error (no response received):', error.request);
        errorMessage = 'No response received from server. Check your network connection.';
        detailedError = 'The request was made but no response was received.';
      } else {
        console.error('Error details:', error.message);
        detailedError = `Error message: ${error.message}`;
      }
      
      // Enhanced handling for network and CORS errors
      if (error.message?.includes('Network Error')) {
        errorMessage = 'Network error occurred. This might be due to CORS restrictions or the server being unavailable.';
        console.error('CORS troubleshooting info:', {
          url: SERVER_NAME,
          method: 'POST instead of DELETE',
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'Origin': window.location.origin
          },
          serverExpectedLocation: deleteUserEndpoint
        });
        
        // Add a detailed note about how to fix this on the server side
        detailedError = 'CORS error: The server needs to allow DELETE method (or POST with _method=DELETE) from origin ' + 
          window.location.origin + '. Check server CORS configuration.';
      }
      
      // Technical error details for debugging
      console.error('Technical error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        response: error.response?.data
      });
      
      setStatus('Error occurred');
      setError(errorMessage);
      setIsProcessing(false);
      
      // Show alert with error details for debugging
      if (detailedError) {
        console.error('Technical error:', detailedError);
        setTimeout(() => {
          alert(`Account deletion failed: ${errorMessage}\n\nTechnical details: ${detailedError}`);
        }, 500);
      }
    }
  };
  
  return (
    <Dialog
      open={open}
      onClose={!isProcessing ? handleClose : undefined}
      aria-labelledby="unsubscribe-dialog-title"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: classes.dialog
      }}
    >
      <DialogTitle id="unsubscribe-dialog-title" disableTypography>
        <Typography variant="h6" className={classes.title}>
          Account Deletion & Unsubscribe
        </Typography>
      </DialogTitle>
      
      <DialogContent className={classes.content}>
        {isProcessing ? (
          <div className={classes.processingContainer}>
            <CircularProgress color="secondary" />
            <Typography variant="body1" className={classes.statusMessage}>
              {status}
            </Typography>
            {success && (
              <Typography variant="body1" className={classes.successMessage}>
                Your account has been deleted. You will be logged out automatically.
              </Typography>
            )}
            {error && (
              <Typography variant="body1" className={classes.errorMessage}>
                {error}
              </Typography>
            )}
          </div>
        ) : (
          <>
            <DialogContentText>
              You are about to permanently delete your account and unsubscribe from all E3D services.
              This action cannot be undone.
            </DialogContentText>
            
            <div className={classes.infoSection}>
              <Typography variant="subtitle1" gutterBottom>
                This process will:
              </Typography>
              <ul>
                <li>Delete your account with username: <strong>{username}</strong></li>
                <li>Remove your data from our database</li>
                {userAddress && (
                  <li>Cancel your blockchain subscription if you have one</li>
                )}
                <li>Log you out immediately</li>
              </ul>
            </div>
            
            <div className={classes.confirmationBox}>
              <Typography variant="body1" className={classes.warning}>
                Warning: This action is permanent and cannot be reversed.
              </Typography>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmUnderstand}
                    onChange={(e) => setConfirmUnderstand(e.target.checked)}
                    className={classes.checkbox}
                    size="medium"
                  />
                }
                label="I understand that my account will be permanently deleted"
                className={classes.checkboxLabel}
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmNFT}
                    onChange={(e) => setConfirmNFT(e.target.checked)}
                    className={classes.checkbox}
                    size="medium"
                  />
                }
                label="I understand that my NFT subscription will be cancelled (if applicable)"
                className={classes.checkboxLabel}
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmFinal}
                    onChange={(e) => setConfirmFinal(e.target.checked)}
                    className={classes.checkbox}
                    size="medium"
                  />
                }
                label="I want to proceed with account deletion"
                className={classes.checkboxLabel}
              />
              
              {error && (
                <div>
                  <Typography variant="body2" className={classes.errorMessage}>
                    {error}
                  </Typography>
                  
                  <Button
                    variant="contained"
                    size="small"
                    style={{ marginTop: '15px', backgroundColor: '#4a148c' }}
                    onClick={() => {
                      // Get all available information from localStorage for debugging
                      const allItems = {};
                      for (let i = 0; i < localStorage.length; i++) {
                        const key = localStorage.key(i);
                        try {
                          allItems[key] = localStorage.getItem(key);
                        } catch (e) {
                          allItems[key] = '[Error reading value]';
                        }
                      }
                      
                      console.log('Debug: All localStorage items:', allItems);
                      
                      // Check session cookie
                      console.log('Debug: Document cookies:', document.cookie);
                      
                      // Show debug info and attempt direct API call
                      const debugInfo = `
UserID Sources:
- Prop: ${propUserID || 'N/A'}
- Context: ${contextUserID || 'N/A'}
- LocalStorage: ${allItems.userID || 'N/A'}

Username Sources:
- Prop: ${propUsername || 'N/A'}
- Context: ${contextUsername || 'N/A'}
- LocalStorage: ${allItems.username || 'N/A'}

Authentication State:
- isAuthenticated in localStorage: ${allItems.isAuthenticated || 'N/A'}
`;
                      
                      alert('Debug info collected to console. Check browser console for details.' + debugInfo);
                      
                      // Make a special diagnostic call using the correct endpoint
                      // Properly preserve the complete SERVER_NAME path including /apitest
                      const diagnosticEndpoint = SERVER_NAME.endsWith('/') 
                        ? `${SERVER_NAME}deleteUser` 
                        : `${SERVER_NAME}/deleteUser`;
                      console.log('Diagnostic endpoint:', diagnosticEndpoint);
                      
                      // For diagnostic, we'll make a POST request with a special flag
                      // to check if the endpoint exists without deleting anything
                      axios.post(diagnosticEndpoint, {
                        diagnosticMode: true,  // This signals we're just testing the endpoint
                        username: allItems.username || contextUsername || propUsername
                      }, { withCredentials: true })
                      .then(response => {
                        console.log('Diagnostic server response:', response.data);
                        setError(`Diagnostic complete. Check console. Server message: ${response.data.message || 'No message'}`);
                      })
                      .catch(error => {
                        console.error('Diagnostic error:', error);
                        
                        // Log detailed error information
                        if (error.response) {
                          console.error('Response error data:', error.response.data);
                          console.error('Response error status:', error.response.status);
                          console.error('Response error headers:', error.response.headers);
                        } else if (error.request) {
                          console.error('Request error:', error.request);
                        } else {
                          console.error('Error details:', error.message);
                        }
                        
                        // Check if CORS might be an issue
                        if (error.message.includes('Network Error') || 
                            (error.response && error.response.status === 0)) {
                          setError(`Diagnostic failed: Possible CORS or network issue. Check console.`);
                        } else if (error.response && error.response.status === 404) {
                          setError(`Diagnostic failed: Endpoint not found (404). Check server configuration.`);
                        } else {
                          setError(`Diagnostic failed: ${error.message}. Check console.`);
                        }
                      });
                    }}
                  >
                    Try Debug Recovery
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
      
      {!isProcessing && (
        <DialogActions>
          <Button 
            onClick={handleClose} 
            variant="outlined" 
            className={`${classes.actionButton} ${classes.cancelButton}`}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUnsubscribe}
            variant="contained"
            className={`${classes.actionButton} ${classes.confirmButton}`}
            disabled={isProcessing || !confirmUnderstand || !confirmNFT || !confirmFinal}
            size="large"
            title={!userID && confirmUnderstand && confirmNFT && confirmFinal ? 
              "Attempting account deletion without userID - will try to use username instead" : 
              "Delete account permanently"}
            style={{
              backgroundColor: !userID && confirmUnderstand && confirmNFT && confirmFinal ? 
                '#ff9800' : // Orange for warning mode
                '#ff5252',  // Regular red
              fontWeight: 'bold'
            }}
          >
            {!userID && confirmUnderstand && confirmNFT && confirmFinal ? 
              'ATTEMPT DELETE (NO ID)' : 
              'DELETE ACCOUNT'
            }
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default UnsubscribeDialog;
